import HttpService from '@frontend/AppSettings/services/HttpService';
import { pathTimesheetViewPrefs } from '@frontend/__settings__/septSoftwarePaths';

/**
 * Per-user timesheet view preferences (plans/todo/timesheet-project-filter-persistence.md).
 * Caller-scoped: the server keys the row on the authenticated employee (token oid, converted
 * members resolved via entra_oid), so NO id is ever sent from here. Last write wins across
 * devices/tabs — nothing destructive is downstream of these values.
 */
interface Envelope<T> {
  successful: boolean;
  data: T;
}

export interface TimesheetViewPrefs {
  selectedProjectIds: string[];
  showAllProjects: boolean;
  timesheetViewMode: 'my' | 'all';
}

function parsePrefs(raw: unknown): TimesheetViewPrefs | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;
  const ids = candidate.selectedProjectIds;
  const selectedProjectIds = Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  const timesheetViewMode =
    candidate.timesheetViewMode === 'all' ? 'all' : 'my';
  return {
    selectedProjectIds,
    showAllProjects: candidate.showAllProjects === true,
    timesheetViewMode,
  };
}

const http = new HttpService();

class TimesheetViewPrefsService {
  /**
   * The caller's stored preferences, or null when there are none / the server predates the
   * feature / anything fails — null always means "start from today's defaults, session-only".
   */
  static async getPrefs(): Promise<TimesheetViewPrefs | null> {
    try {
      const response = await http.get<Envelope<{ prefs: unknown }>>(pathTimesheetViewPrefs);
      const prefs = response?.data?.prefs;
      // An empty object is the server's "no row yet" — not a preference to apply.
      if (!prefs || typeof prefs !== 'object' || Object.keys(prefs).length === 0) return null;
      return parsePrefs(prefs);
    } catch (error) {
      console.warn('Timesheet view prefs: load failed — staying session-only.', error);
      return null;
    }
  }

  /** Fire-and-forget save; the caller catches (a 503 means the migration hasn't run — fine). */
  static async savePrefs(prefs: TimesheetViewPrefs): Promise<void> {
    await http.put(pathTimesheetViewPrefs, {
      selectedProjectIds: prefs.selectedProjectIds,
      showAllProjects: prefs.showAllProjects,
      timesheetViewMode: prefs.timesheetViewMode,
    });
  }
}

export default TimesheetViewPrefsService;
